/**
 * Comment composer — the quoted-text banner and send chrome around a
 * `MentionEditor`. Handles both text-range anchors (with quoted-text preview)
 * and section/whole-doc anchors; @mentions of bid members are serialized as
 * `@[Name](user:<id>)` tokens by the editor.
 *
 * On a draft comment, an author who manages the bid's team can also mention
 * colleagues who are not on the bid; posting then asks first whether to add
 * them (`MentionInviteNotice`). Anyone mentioned who won't be reached is named
 * in a note instead of being dropped silently.
 */

import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import { ArrowUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'
import { MentionEditor, type MentionEditorHandle } from '@/components/drafting/MentionEditor'
import { MentionInviteNotice } from '@/components/drafting/MentionInviteNotice'
import { useCommentsStore } from '@/stores/comments-store'
import { useMentionInviteFlow } from '@/hooks/useMentionInvites'
import { refreshBidMembers } from '@/hooks/useBidMembers'
import type { CommentAnchor, CommentTargetType } from '@/lib/comments-api'

/**
 * The one composer chrome: a white pill on the drawer/panel tint with a
 * circular send button inside it. Shared with `CommentCard`'s inline reply box
 * so a reply and a new comment read as the same control.
 *
 * There is no Cancel button by design — Escape in the editor cancels (see
 * `MentionEditor`'s `onCancel`), and a permanently visible Cancel next to a
 * one-line input is chrome the flat design has no room for.
 */
export function ComposerRow({
  sendLabel,
  disabled,
  onSend,
  children,
}: {
  /** Accessible name of the send button — "Add comment" / "Send reply". */
  sendLabel: string
  disabled: boolean
  onSend: () => void
  children: ReactNode
}) {
  return (
    <div className="flex items-end gap-1.5 rounded-xl border border-input bg-card pl-3 pr-1.5 py-1 focus-within:ring-1 focus-within:ring-ring">
      <div className="min-w-0 flex-1">{children}</div>
      <Button
        size="icon"
        aria-label={sendLabel}
        className="h-7 w-7 shrink-0 self-end mb-0.5 rounded-full bg-foreground text-background hover:bg-foreground/90"
        onClick={onSend}
        disabled={disabled}
      >
        <ArrowUp className="h-4 w-4" />
      </Button>
    </div>
  )
}

interface CommentComposerProps {
  bidId: string
  /** Draft artifact id, or extraction-item id on a whole-object target type. */
  targetId: string
  /** Which comment family this thread belongs to. Defaults to the draft one. */
  targetType?: CommentTargetType
  anchor: CommentAnchor
  onClose: () => void
  onCreated?: () => void
  autoFocus?: boolean
  /** Bid members offered as @mention suggestions. Empty → no suggestions. */
  bidMembers?: BidMemberInfo[] | undefined
}

export function CommentComposer({
  bidId,
  targetId,
  targetType = 'draft',
  anchor,
  onClose,
  onCreated,
  autoFocus = true,
  bidMembers = [],
}: CommentComposerProps) {
  const create = useCommentsStore((s) => s.create)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const editorRef = useRef<MentionEditorHandle>(null)
  const savingRef = useRef(false)
  const invites = useMentionInviteFlow(bidMembers, targetType === 'draft')

  // `confirmed` is the prompt's "Add and post". Without it, a body that
  // mentions someone the author could add raises the prompt and posts nothing.
  const handleSave = async (body: string, confirmed = false) => {
    if (savingRef.current || !body.trim()) return
    const addToBid = invites.prepare(body, confirmed)
    if (addToBid === null) return
    savingRef.current = true
    setSaving(true)
    setError(null)
    try {
      await create(
        bidId,
        targetId,
        addToBid.length > 0 ? { anchor, body, addToBid } : { anchor, body },
        targetType,
      )
      if (addToBid.length > 0) refreshBidMembers(bidId)
      editorRef.current?.clear()
      invites.reset()
      onCreated?.()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save comment')
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  // A whole-object thread hangs off the row itself — there is no quoted range
  // or "whole document" scope to explain, and a banner saying so would be
  // noise above a two-line box.
  const showAnchorBanner = targetType === 'draft'

  return (
    <div className="flex flex-col gap-2">
      {showAnchorBanner && anchor.quotedText && (
        <div className="text-xs italic border-l-2 border-[hsl(var(--brand-light))] pl-2 text-muted-foreground line-clamp-2">
          “{anchor.quotedText}”
        </div>
      )}
      {showAnchorBanner && !anchor.quotedText && anchor.sectionId == null && (
        <div className="text-meta">Commenting on the whole document</div>
      )}
      {showAnchorBanner &&
        !anchor.quotedText &&
        anchor.sectionId != null &&
        anchor.startPos == null && <div className="text-meta">Section-level comment</div>}
      <ComposerRow
        sendLabel="Add comment"
        disabled={saving || isEmpty}
        onSend={() => void handleSave(editorRef.current?.getBody() ?? '')}
      >
        <MentionEditor
          ref={editorRef}
          bidMembers={bidMembers}
          nonMemberCandidates={invites.nonMemberCandidates}
          onBodyChange={invites.onBodyChange}
          placeholder="Leave a comment…"
          compact
          autoFocus={autoFocus}
          onSubmit={(body) => void handleSave(body)}
          onCancel={onClose}
          onEmptyChange={setIsEmpty}
        />
      </ComposerRow>
      <MentionInviteNotice
        pendingAdd={invites.pendingAdd}
        unreachable={invites.unreachable}
        confirmLabel="Add and post"
        busy={saving}
        onConfirm={() => void handleSave(invites.body, true)}
        onCancel={() => {
          invites.cancel()
          editorRef.current?.focus()
        }}
      />
      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  )
}
